import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { setupScrollAnimations } from '../utils/scrollAnimations';

const Contact = () => {
  const { t } = useTranslation();
  const location = useLocation();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const [fieldErrors, setFieldErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
    message: false
  });

  const [touchedFields, setTouchedFields] = useState({
    firstName: false,
    lastName: false,
    email: false,
    message: false
  });

  // Check for service parameter in URL and pre-populate the form
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const serviceParam = urlParams.get('service');
    
    if (serviceParam) {
      setFormData(prevData => ({
        ...prevData,
        service: serviceParam
      }));
    }
  }, [location.search]);

  useEffect(() => {
    setupScrollAnimations();
  }, []);

  const handleEmailClick = () => {
    window.location.href = 'mailto:info@ayaat.co';
  };

  const handlePhoneClick = () => {
    window.location.href = 'tel:+966122134727';
  };

  const handleAddressClick = () => {
    // Open Google Maps with the address
    const address = 'King Fahad Rd, Al Faisaliyyah, Jeddah 23444';
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    window.open(mapsUrl, '_blank');
  };

  const handleCopyEmail = (e) => {
    e.stopPropagation(); // Prevent triggering the mailto link
    
    const copyIcon = e.target;
    const originalText = copyIcon.textContent;
    
    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText('info@ayaat.co').then(() => {
        // Show success message
        copyIcon.textContent = '✓';
        copyIcon.style.color = '#28a745';
        setTimeout(() => {
          copyIcon.textContent = originalText;
          copyIcon.style.color = '#6c757d';
        }, 1500);
      }).catch((err) => {
        console.log('Clipboard API failed:', err);
        fallbackCopy();
      });
    } else {
      // Use fallback method
      fallbackCopy();
    }
    
    function fallbackCopy() {
      try {
        const textArea = document.createElement('textarea');
        textArea.value = 'info@ayaat.co';
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          // Show success message
          copyIcon.textContent = '✓';
          copyIcon.style.color = '#28a745';
          setTimeout(() => {
            copyIcon.textContent = originalText;
            copyIcon.style.color = '#6c757d';
          }, 1500);
        } else {
          // Show error message
          copyIcon.textContent = '✗';
          copyIcon.style.color = '#dc3545';
          setTimeout(() => {
            copyIcon.textContent = originalText;
            copyIcon.style.color = '#6c757d';
          }, 1500);
        }
      } catch (err) {
        console.log('Fallback copy failed:', err);
        // Show error message
        copyIcon.textContent = '✗';
        copyIcon.style.color = '#dc3545';
        setTimeout(() => {
          copyIcon.textContent = originalText;
          copyIcon.style.color = '#6c757d';
        }, 1500);
      }
    }
  };

  const handleCopyAddress = (e) => {
    e.stopPropagation(); // Prevent any other click events
    
    const copyIcon = e.target;
    const originalText = copyIcon.textContent;
    const addressText = 'King Fahad Rd, Al Faisaliyyah, Jeddah 23444, 4th Floor';
    
    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(addressText).then(() => {
        // Show success message
        copyIcon.textContent = '✓';
        copyIcon.style.color = '#28a745';
        setTimeout(() => {
          copyIcon.textContent = originalText;
          copyIcon.style.color = '#6c757d';
        }, 1500);
      }).catch((err) => {
        console.log('Clipboard API failed:', err);
        fallbackCopy();
      });
    } else {
      // Use fallback method
      fallbackCopy();
    }
    
    function fallbackCopy() {
      try {
        const textArea = document.createElement('textarea');
        textArea.value = addressText;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          // Show success message
          copyIcon.textContent = '✓';
          copyIcon.style.color = '#28a745';
          setTimeout(() => {
            copyIcon.textContent = originalText;
            copyIcon.style.color = '#6c757d';
          }, 1500);
        } else {
          // Show error message
          copyIcon.textContent = '✗';
          copyIcon.style.color = '#dc3545';
          setTimeout(() => {
            copyIcon.textContent = originalText;
            copyIcon.style.color = '#6c757d';
          }, 1500);
        }
      } catch (err) {
        console.log('Fallback copy failed:', err);
        // Show error message
        copyIcon.textContent = '✗';
        copyIcon.style.color = '#dc3545';
        setTimeout(() => {
          copyIcon.textContent = originalText;
          copyIcon.style.color = '#6c757d';
        }, 1500);
      }
    }
  };

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateName = (name) => {
    const nameRegex = /^[a-zA-Z\s\u0600-\u06FF]+$/; // Allows letters, spaces, and Arabic characters
    return nameRegex.test(name);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[+]?[0-9\s\-()]+$/; // Allows +, numbers, spaces, hyphens, and parentheses
    return phoneRegex.test(phone);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Apply validation based on field type
    if (name === 'firstName' || name === 'lastName') {
      // Only allow letters and spaces for names
      if (value === '' || validateName(value)) {
        setFormData({
          ...formData,
          [name]: value
        });
        // Update error state for required fields
        if (touchedFields[name]) {
          let hasError = false;
          if (name === 'firstName' || name === 'lastName') {
            // For names, check both empty and invalid format
            hasError = value.trim() === '' || (value.trim() !== '' && !validateName(value));
          } else {
            hasError = value.trim() === '';
          }
          setFieldErrors({
            ...fieldErrors,
            [name]: hasError
          });
        }
      }
    } else if (name === 'phone') {
      // Only allow +, numbers, spaces, hyphens, and parentheses for phone
      if (value === '' || validatePhone(value)) {
        setFormData({
          ...formData,
          [name]: value
        });
      }
    } else {
      // For other fields (email, service, message), allow all input
      setFormData({
        ...formData,
        [name]: value
      });
      // Update error state for required fields
      if (touchedFields[name] && (name === 'email' || name === 'message')) {
        let hasError = false;
        if (name === 'email') {
          // For email, check both empty and invalid format
          hasError = value.trim() === '' || (value.trim() !== '' && !validateEmail(value));
        } else {
          hasError = value.trim() === '';
        }
        setFieldErrors({
          ...fieldErrors,
          [name]: hasError
        });
      }
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    // Mark field as touched
    setTouchedFields({
      ...touchedFields,
      [name]: true
    });
    
    // Check for errors on required fields
    if (name === 'firstName' || name === 'lastName' || name === 'email' || name === 'message') {
      let hasError = false;
      
      if (name === 'firstName' || name === 'lastName') {
        // For names, check both empty and invalid format
        hasError = value.trim() === '' || (value.trim() !== '' && !validateName(value));
      } else if (name === 'email') {
        // For email, check both empty and invalid format
        hasError = value.trim() === '' || (value.trim() !== '' && !validateEmail(value));
      } else {
        // For other required fields, just check if empty
        hasError = value.trim() === '';
      }
      
      setFieldErrors({
        ...fieldErrors,
        [name]: hasError
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    // Check for empty required fields first
    const requiredFields = ['firstName', 'lastName', 'email', 'message'];
    let hasEmptyFields = false;
    
    // Mark all fields as touched and check for empty required fields
    const newTouchedFields = { ...touchedFields };
    const newFieldErrors = { ...fieldErrors };
    
    requiredFields.forEach(field => {
      newTouchedFields[field] = true;
      if (formData[field].trim() === '') {
        newFieldErrors[field] = true;
        hasEmptyFields = true;
      }
    });
    
    setTouchedFields(newTouchedFields);
    setFieldErrors(newFieldErrors);
    
    if (hasEmptyFields) {
      alert("❌ Please fill in all required fields (marked with *)");
      return;
    }
  
    // Validate form fields
    if (!validateName(formData.firstName)) {
      alert("❌ Please enter a valid first name (letters only)");
      return;
    }
    
    if (!validateName(formData.lastName)) {
      alert("❌ Please enter a valid last name (letters only)");
      return;
    }
    
    if (!validateEmail(formData.email)) {
      alert("❌ Please enter a valid email address");
      return;
    }
    
    if (formData.phone && !validatePhone(formData.phone)) {
      alert("❌ Please enter a valid phone number (numbers, +, spaces, hyphens, and parentheses only)");
      return;
    }
  
    const submitButton = e.target.querySelector('button[type="submit"]');
    const originalText = submitButton.textContent;
    submitButton.textContent = 'Sending...';
    submitButton.disabled = true;
  
    try {
      const response = await fetch("https://formspree.io/f/xyznkjpq", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          service: formData.service,
          message: formData.message
        })
      });
  
      if (response.ok) {
        alert("✅ Thank you for your message! We will get back to you soon.");
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          service: "",
          message: ""
        });
      } else {
        const errorData = await response.json();
        console.error("Server error:", errorData);
        throw new Error(errorData.error || "Failed to send message");
      }
    } catch (error) {
      console.error("Error sending email:", error);
      alert(
        "❌ Sorry, there was an error sending your message. Please try again or contact us directly at info@ayaat.co"
      );
    } finally {
      submitButton.textContent = originalText;
      submitButton.disabled = false;
    }
  };
  

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="contact-content">
        <h1>{t('contact.title')}</h1>
        <div className="contact-intro">
          <p>{t('contact.subtitle')}</p>
        </div>
        
        {/* Contact Information - Full Width */}
        <div className="contact-info-section contact-info-fullwidth animate-on-scroll">
          <div className="contact-section">
            <h2>{t('contact.info.title')}</h2>
            <div className="contact-info">
                      <div className="contact-item contact-item-clickable" onClick={handlePhoneClick}>
                        <div className="contact-icon">📞</div>
                        <div className="contact-details">
                          <strong>{t('contact.info.phone')}:</strong> +966 12 213 4727
                        </div>
                      </div>
              <div className="contact-item contact-item-clickable" onClick={handleEmailClick}>
                <div className="contact-icon">✉️</div>
                <div className="contact-details">
                  <strong>{t('contact.info.email')}:</strong> info@ayaat.co
                </div>
                <div className="copy-icon" onClick={handleCopyEmail} title="Copy email address">
                  📋
                </div>
              </div>
              <div className="contact-item contact-item-clickable" onClick={handleAddressClick}>
                <div className="contact-icon">📍</div>
                <div className="contact-details">
                  <strong>{t('contact.info.address')}:</strong> King Fahad Rd, Al Faisaliyyah, Jeddah 23444, 4th Floor
                </div>
                <div className="copy-icon" onClick={handleCopyAddress} title="Copy address">
                  📋
                </div>
              </div>
            </div>
            
            {/* Contact Map */}
            <div className="contact-map-container">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1311.9135420510315!2d39.18675024169395!3d21.56046450378454!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x15c3d048f815cca1%3A0x6f371b92f70dd95a!2z2YXYsdmD2LIg2KfZhNi52KfYsNix2YrYqSDYqNmE2KfYstin!5e0!3m2!1sen!2ssa!4v1759057003865!5m2!1sen!2ssa"
                width="100%"
                height="250"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="United Ayaat Office Location"
              ></iframe>
            </div>
          </div>
        </div>
        
        <div className="contact-main">
          {/* Contact Form */}
          <div className="contact-form-section animate-on-scroll">
            <h2>{t('contact.form.title')}</h2>
                    <form className="contact-form" onSubmit={handleSubmit}>
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="firstName">{t('contact.form.firstName')} *</label>
                          <input
                            type="text"
                            id="firstName"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                            placeholder={t('contact.form.placeholder.firstName')}
                            className={touchedFields.firstName && fieldErrors.firstName ? 'field-error' : ''}
                            title={touchedFields.firstName && fieldErrors.firstName ? (formData.firstName.trim() === '' ? 'This field is required' : 'Please enter letters only') : ''}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="lastName">{t('contact.form.lastName')} *</label>
                          <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                            placeholder={t('contact.form.placeholder.lastName')}
                            className={touchedFields.lastName && fieldErrors.lastName ? 'field-error' : ''}
                            title={touchedFields.lastName && fieldErrors.lastName ? (formData.lastName.trim() === '' ? 'This field is required' : 'Please enter letters only') : ''}
                          />
                        </div>
                      </div>
              
              <div className="form-group">
                <label htmlFor="email">{t('contact.form.email')} *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  placeholder={t('contact.form.placeholder.email')}
                  className={touchedFields.email && fieldErrors.email ? 'field-error' : ''}
                  title={touchedFields.email && fieldErrors.email ? (formData.email.trim() === '' ? 'This field is required' : 'Please enter a valid email address') : ''}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="phone">{t('contact.form.phone')}</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t('contact.form.placeholder.phone')}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="service">{t('contact.form.service')}</label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                >
                  <option value="">Select a service</option>
                  <option value="steel">{t('contact.form.serviceOptions.steel')}</option>
                  <option value="marble">{t('contact.form.serviceOptions.marble')}</option>
                  <option value="contracting">{t('contact.form.serviceOptions.contracting')}</option>
                  <option value="general">{t('contact.form.serviceOptions.general')}</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="message">{t('contact.form.message')} *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  rows="5"
                  placeholder={t('contact.form.placeholder.message')}
                  className={touchedFields.message && fieldErrors.message ? 'field-error' : ''}
                  title={touchedFields.message && fieldErrors.message ? 'This field is required' : ''}
                ></textarea>
              </div>
              
              <button type="submit" className="submit-btn btn-animated hover-lift">
                {t('contact.form.submit')}
              </button>
            </form>
          </div>
          
          <div className="contact-right-section animate-on-scroll">
            <div className="contact-section">
              <h3>{t('contact.hours.title')}</h3>
              <div className="contact-info">
                <div className="contact-item">
                  <div className="contact-icon">🕐</div>
                  <div className="contact-details">
                    <strong>{t('contact.hours.weekdays')}:</strong> {t('contact.hours.weekdaysTime')}
                  </div>
                </div>
                <div className="contact-item">
                  <div className="contact-icon">🕐</div>
                  <div className="contact-details">
                    <strong>{t('contact.hours.friday')}:</strong> {t('contact.hours.fridayTime')}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="contact-section">
              <h3>{t('contact.areas.title')}</h3>
              <div className="contact-info">
                <p>{t('contact.areas.description')}</p>
                <ul>
                  {t('contact.areas.cities', { returnObjects: true }).map((city, index) => (
                    <li key={index}>{city}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.main>
  );
};

export default Contact;
